import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { ApiError } from '@molvia/client'
import {
  ERROR,
  isWireCode,
  pendingVerdictCodec,
  pendingVerdictSchema,
  ratingSchema,
} from '@molvia/model'
import type { PendingVerdict, Rating, WireCode } from '@molvia/model'
import { api } from '@/api'
import type { Score } from '@/components/rating'
import { useActorStore } from '@/stores/actor'
import { useLoginStore } from '@/stores/login'
import { forgetOwnPrices } from '@/stores/ownPrices'
import { HOLDS, isRecord } from '@/stores/queueing'
import type { Loose } from '@/stores/queueing'
import { read, write } from '@/stores/storage'
import { reportFailure } from '@/failures'

/**
 * Re-exported rather than declared again: the scale of MOL-32 owns it (`components/rating.ts`),
 * and two identical `Rating['score']` in two files are two places to change (МР-9).
 */
export type { Score }

/**
 * A verdict on the phone before the server has it. `typing` is what the card holds while the
 * person is still choosing — kept so a PWA unloaded mid-sentence gives the text back (MOL-28,
 * В-5) — and is never sent. `saved` waits to be sent. `error` is the code of a refusal that a
 * repeat would only meet again: the draft is back to `typing` for the person to fix.
 */
export interface VerdictDraft {
  readonly card: PendingVerdict
  readonly score: Score | null
  readonly review: string
  readonly state: 'typing' | 'saved'
  readonly error: WireCode | null
}

/** Why the last run stopped with drafts left: the connection, or a server that broke. */
export type Held = 'offline' | 'failed'

const KEY = 'molvia.verdict-drafts'
const CONFIRMED_KEY = 'molvia.verdict-confirmed'

/**
 * The city of the card is kept beside it, never inside (MOL-120, adversarial А4): the version before
 * reads the card with a strict codec, and a card that grew a field was dropped as a broken entry —
 * a verdict saved with no signal, lost by a rollback that neither side refused. Beside the card it
 * is a field that version never looks at, and the words go through.
 */
function encode(draft: VerdictDraft): Loose {
  const { placeCity, ...card } = draft.card
  return {
    ...draft,
    card: pendingVerdictCodec.encode(card),
    ...(placeCity === undefined ? {} : { placeCity }),
  }
}

function decode(raw: unknown): VerdictDraft | null {
  if (!isRecord(raw)) return null
  const card = pendingVerdictCodec.safeParse(raw.card)
  // A city that does not read is lost alone: the words are what this memory is for.
  const city = pendingVerdictSchema.shape.placeCity.safeParse(raw.placeCity).data
  const score = raw.score === null ? null : ratingSchema.shape.score.safeParse(raw.score).data
  const { review, state, error } = raw
  if (!card.success || score === undefined || typeof review !== 'string') return null
  if (state !== 'typing' && state !== 'saved') return null
  if (error !== null && !isWireCode(error)) return null
  // A saved draft without a score could never be sent, and would hide its card for good.
  if (state === 'saved' && score === null) return null
  const kept = city === undefined ? card.data : { ...card.data, placeCity: city }
  return { card: kept, score, review, state, error }
}

/** A broken entry is dropped alone: the ones beside it are somebody's words. */
function recall(key: string): Record<string, VerdictDraft> {
  const raw = read(key)
  if (!raw) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return {}
    const drafts: Record<string, VerdictDraft> = {}
    for (const draft of parsed.map(decode)) if (draft) drafts[draft.card.itemId] = draft
    return drafts
  } catch {
    return {}
  }
}

/** Item → when the server confirmed its rating, as epoch milliseconds. */
function recallConfirmed(key: string): Record<string, number> {
  const raw = read(key)
  if (!raw) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed)) return {}
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] => typeof entry[1] === 'number',
      ),
    )
  } catch {
    return {}
  }
}

/** The review goes only when it says something: a `PUT` without one keeps what was written. */
function ratingOf(draft: VerdictDraft & { score: Score }): Rating {
  return draft.review.trim() ? { score: draft.score, review: draft.review } : { score: draft.score }
}

/**
 * Verdicts the person saved and the server has not confirmed (MOL-28, Р-3, Р-5). «Сохранить»
 * writes here and the card goes at once; sending is the app's business, not the screen's — at
 * start, on `online` and when the app comes back into view (`App.vue`).
 *
 * Not the trip queue: that is an ordered log of writes, each with an id of its own. A verdict is
 * addressed by its item and `PUT` is safe to repeat, so this is a map — the latest word on an
 * item replaces the earlier one, and the order of sending does not matter.
 */
export const useVerdictDraftsStore = defineStore('verdictDrafts', () => {
  const actor = useActorStore()
  const login = useLoginStore()

  const drafts = ref<Record<string, VerdictDraft>>({})
  const held = ref<Held | null>(null)
  /**
   * When each rating reached the server. Kept here, by whoever sends, and on the device: the
   * queue of «Оценки» may have been closed when a rating went, and a list it remembers from
   * before must not offer that item again when it opens (adversarial F1, R1).
   */
  const confirmed = ref<Record<string, number>>({})

  const waiting = computed(() =>
    Object.values(drafts.value).filter((draft) => draft.state === 'saved'),
  )

  function load(id: string | null): void {
    drafts.value = id ? recall(`${KEY}.${id}`) : {}
    confirmed.value = id ? recallConfirmed(`${CONFIRMED_KEY}.${id}`) : {}
    held.value = null
  }

  function persist(id: string | null): void {
    if (!id) return
    write(`${KEY}.${id}`, JSON.stringify(Object.values(drafts.value).map(encode)))
  }

  function put(draft: VerdictDraft): void {
    drafts.value = { ...drafts.value, [draft.card.itemId]: draft }
    persist(actor.id)
  }

  function confirm(itemId: string): void {
    confirmed.value = { ...confirmed.value, [itemId]: Date.now() }
    if (actor.id) write(`${CONFIRMED_KEY}.${actor.id}`, JSON.stringify(confirmed.value))
  }

  /** Forgets confirmations an answer asked for at `since` already reflects. */
  function settle(since: Date): void {
    const kept = Object.fromEntries(
      Object.entries(confirmed.value).filter(([, at]) => at >= since.getTime()),
    )
    if (Object.keys(kept).length === Object.keys(confirmed.value).length) return
    confirmed.value = kept
    if (actor.id) write(`${CONFIRMED_KEY}.${actor.id}`, JSON.stringify(kept))
  }

  function forget(itemId: string): void {
    if (!(itemId in drafts.value)) return
    const rest = { ...drafts.value }
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- the key is the item, a map
    delete rest[itemId]
    drafts.value = rest
    persist(actor.id)
  }

  load(actor.id)
  watch(
    () => actor.id,
    (id) => {
      load(id)
      void flush()
    },
  )

  /** What the card holds while the person is still choosing. Nothing chosen — nothing kept. */
  function keep(card: PendingVerdict, score: Score | null, review: string): void {
    if (drafts.value[card.itemId]?.state === 'saved') return
    if (score === null && !review.trim()) {
      forget(card.itemId)
      return
    }
    put({ card, score, review, state: 'typing', error: null })
  }

  /** «Сохранить оценку»: kept first, sent after — the card never waits for the network. */
  function save(card: PendingVerdict, score: Score, review: string): void {
    put({ card, score, review, state: 'saved', error: null })
    // «Тут дешевле» remembered before this verdict no longer says what the item is (MOL-92).
    forgetOwnPrices(actor.id, [card.itemId])
    void flush()
  }

  let running: Promise<void> | null = null

  /**
   * One run at a time: two would send the same draft twice. A run cut short by a change of
   * identity is followed by one for the new identity.
   */
  function flush(): Promise<void> {
    // **Ничего не уходит, пока сервер не сказал, кто мы** (MOL-56, адверсариальный Б1): до
    // ответа «кто мы» — это имя ящика на устройстве, а оно ничего не знает про cookie.
    //
    // **И об этом говорится теми же словами, что и о неудавшейся отправке** (адверсариальный
    // В1): раньше статус ставил провал попытки, и выход отсюда молча оставлял «Отправляем
    // оценку…» навсегда — у полки без сигнала, то есть в главном сценарии продукта. «Не ушло»
    // с кнопкой тут было бы хуже: кнопка зовёт эту же отправку и ничего не изменит, пока
    // личность не осела. А осядет она сама — по `online`, по возвращению во вкладку или по
    // «Повторить» на плашке личности.
    if (actor.state !== 'ready' || login.writesHeld) {
      held.value = 'offline'
      return Promise.resolve()
    }
    if (!running) {
      const owner = actor.id
      running = drain(owner).finally(() => {
        running = null
        if (actor.id !== owner) void flush()
      })
    }
    return running
  }

  async function drain(owner: string | null): Promise<void> {
    if (!owner) return

    for (const draft of waiting.value) {
      if (draft.score === null) continue
      let refusal: WireCode | null = null
      try {
        await api.rateItem(draft.card.itemId, ratingOf({ ...draft, score: draft.score }))
      } catch (error) {
        reportFailure(error, 'screen')
        const code = error instanceof ApiError ? error.code : ERROR.INTERNAL
        // A code the API did not say itself — a proxy's 404 page read as `not_found` — is no
        // refusal: taken for one, it confirmed and dropped a saved rating (adversarial H1).
        const theirs = error instanceof ApiError && error.answered
        if (HOLDS.includes(code) || !theirs) {
          // Decided after the failure, never before: a connection that drops while the answer
          // is on its way is the commonest break, and it is not the server's fault.
          // An identity the server forgot is not the connection: the draft waits, and the screen
          // says it did not go rather than «sending» for the rest of the session (adversarial H2).
          held.value = navigator.onLine || code === ERROR.NO_ACTOR ? 'failed' : 'offline'
          return
        }
        refusal = code
      }

      // The identity changed while the draft was out: the map in memory is now another
      // person's, and this draft is not among them.
      if (actor.id !== owner) return

      // Replaced while it was out: the newer word is still waiting and goes on the next pass.
      if (drafts.value[draft.card.itemId] !== draft) continue

      if (refusal && refusal !== ERROR.NOT_FOUND) {
        put({ ...draft, state: 'typing', error: refusal })
      } else {
        // Rated, or the item is gone: either way nothing is left to ask about it.
        confirm(draft.card.itemId)
        forget(draft.card.itemId)
      }
    }
    held.value = null
    if (waiting.value.length > 0) await drain(owner)
  }

  return { drafts, waiting, held, confirmed, keep, save, forget, settle, flush }
})
